import { EarnRateInfo } from '../../types/CardRewardsEarnRates';
import { CategoryType } from '../__autogenerated__/types/CreditCardCategoryTypes';

export type CategoryEarnRatesMap = Partial<
  Record<
    CategoryType,
    {
      cardsWithCredits: EarnRateInfo[];
      sortedCardsByEarnRate: EarnRateInfo[];
    }
  >
>;

// Categories that don't inherit from allPurchases.
// Rent/housing require special platforms (e.g., Bilt) and don't overlap with normal card spend.
const standaloneCategories: string[] = ['rent', 'housing'];

export const isStandaloneCategory = (category: string): boolean => {
  return standaloneCategories.includes(category);
};

// Helper function to get all parent categories from closest to furthest
export const getAllParentCategories = (category: string): string[] => {
  if (category === 'allPurchases' || category === 'all_purchases') {
    return []; // allPurchases has no parents
  }

  // Standalone categories have no parents
  if (isStandaloneCategory(category)) {
    return [];
  }

  const parts = category.split('.');
  const parents: string[] = [];

  // For single-part categories, only allPurchases is the parent
  if (parts.length === 1) {
    parents.push('allPurchases');
  } else {
    // For multi-part categories, add all intermediate parents
    for (let i = 1; i < parts.length; i++) {
      const parentCategory = parts.slice(0, i).join('.');
      parents.push(parentCategory);
    }
    // Always end with allPurchases as the ultimate parent
    parents.push('allPurchases');
  }

  return parents;
};

const alwaysShowCategories: string[] = [
  'allPurchases',
  'all_purchases',
  'gas',
  'grocery',
  'travel',
  'dining',
];

// Helper function to check if a category should always be shown
export const isAlwaysShowCategory = (category: string): boolean => {
  // Standalone categories are always shown (when a card earns on them)
  if (isStandaloneCategory(category)) {
    return true;
  }
  return alwaysShowCategories.includes(category);
};

// Helper to safely get category data from the map
const getCategoryData = (
  categoryEarnRates: CategoryEarnRatesMap,
  category: string
) => {
  // Try both formats for allPurchases
  if (category === 'allPurchases' || category === 'all_purchases') {
    return (
      (categoryEarnRates as Record<string, CategoryEarnRatesMap[CategoryType]>)['allPurchases'] ||
      (categoryEarnRates as Record<string, CategoryEarnRatesMap[CategoryType]>)['all_purchases']
    );
  }
  return (categoryEarnRates as Record<string, CategoryEarnRatesMap[CategoryType]>)[category];
};

/**
 * Determines if a category should be shown based on the earn rates.
 * Logic: Always show core categories, show top-level categories,
 * and show nested categories when they earn better than their parent categories.
 */
export function shouldShowCategory(
  category: string,
  categoryEarnRates: CategoryEarnRatesMap
): boolean {
  // Always show allPurchases
  if (category === 'allPurchases' || category === 'all_purchases') {
    return true;
  }

  // Get the best card for this category
  const categoryData = getCategoryData(categoryEarnRates, category);
  const categoryBestCard = categoryData?.sortedCardsByEarnRate[0];

  // If no card earns in this category, don't show it
  if (!categoryBestCard) {
    return false;
  }

  // Standalone categories are shown if any card earns on them
  if (isStandaloneCategory(category)) {
    return true;
  }

  // Always show core categories
  if (isAlwaysShowCategory(category)) {
    return true;
  }

  const parts = category.split('.');
  const baseCategory = parts[0];

  // For single-part non-core categories, check against allPurchases
  if (parts.length === 1 && !isAlwaysShowCategory(baseCategory)) {
    const allPurchasesData = getCategoryData(categoryEarnRates, 'allPurchases');
    const allPurchasesBestCard = allPurchasesData?.sortedCardsByEarnRate[0];

    if (
      allPurchasesBestCard &&
      allPurchasesBestCard.totalValue >= categoryBestCard.totalValue
    ) {
      return false;
    }
  }

  // Check all parent categories - hide if same card earns same or better on parent
  const allParentEarnRates = getAllParentCategories(category)
    .map((parentCategory) => getCategoryData(categoryEarnRates, parentCategory)?.sortedCardsByEarnRate[0])
    .filter((x): x is EarnRateInfo => x != null);

  if (
    allParentEarnRates.length > 0 &&
    allParentEarnRates.some((parentEarnRate) => {
      return (
        parentEarnRate.card === categoryBestCard.card &&
        parentEarnRate.totalValue >= categoryBestCard.totalValue
      );
    })
  ) {
    return false;
  }

  // For nested categories with non-core base, only show if better than allPurchases
  if (parts.length > 1 && !isAlwaysShowCategory(baseCategory)) {
    const allPurchasesData = getCategoryData(categoryEarnRates, 'allPurchases');
    const allPurchasesBestCard = allPurchasesData?.sortedCardsByEarnRate[0];

    if (
      allPurchasesBestCard &&
      categoryBestCard.totalValue > allPurchasesBestCard.totalValue
    ) {
      return true;
    }

    return false;
  }

  return true;
}
